import moment from 'moment';
import { createSelector } from 'reselect';
import api from './api';
import jwtDecode from '../helpers/jwt';
import sinceEpoch from '../helpers/date';

const stateSessionSelector = (state) => state.session;

const jwtSelector = createSelector([stateSessionSelector], (session) => {
  if (!session.token) return {};
  return jwtDecode(session.token);
});

const refreshJwtSelector = createSelector([stateSessionSelector], (session) => {
  if (!session.refresh) return {};
  return jwtDecode(session.refresh);
});

const sessionPolicySelector = createSelector([jwtSelector], (jwt) => {
  if (jwt.payload && jwt.payload.pol) {
    if (!Array.isArray(jwt.payload.pol)) return [jwt.payload.pol];
    return jwt.payload.pol;
  }
  return [];
});

const sessionUserSelector = createSelector([jwtSelector], (jwt) => {
  const user = {};
  if (jwt.payload && jwt.payload.given_name) user.firstName = jwt.payload.given_name;
  if (jwt.payload && jwt.payload.family_name) user.lastName = jwt.payload.family_name;
  if (jwt.payload && jwt.payload.full_name) user.name = jwt.payload.full_name;
  if (jwt.payload && jwt.payload.locale) user.locale = jwt.payload.locale;
  if (jwt.payload && jwt.payload.email) user.email = jwt.payload.email;
  if (jwt.payload && jwt.payload.partner) user.partnerId = jwt.payload.partner;
  if (jwt.payload && jwt.payload.location) user.locationId = jwt.payload.location;
  if (jwt.payload && jwt.payload.nameid) user.id = jwt.payload.nameid;
  return user;
});

export const selector = createSelector(
  [stateSessionSelector, jwtSelector, refreshJwtSelector, sessionPolicySelector, sessionUserSelector],
  (session, jwt, refresh_jwt, policies, user) => {
    const authenticated = !!session.token;
    const token_iat = jwt.payload ? sinceEpoch(jwt.payload.iat) : new Date();
    const token_expires = jwt.payload ? sinceEpoch(jwt.payload.exp) : new Date();
    const refresh_expires = refresh_jwt.payload ? sinceEpoch(refresh_jwt.payload.exp) : new Date();
    const server_offset = moment(token_iat).diff(session.tokenDate);
    const hasPolicy = (search) => !!policies.find((policy) => policy.toLowerCase() === search.toLowerCase());
    return { ...session, jwt, refresh_jwt, server_offset, token_expires, refresh_expires, policies, hasPolicy, authenticated, user };
  },
);

const AUTH__AUTHENTICATE_USER_SUCCESS = 'AUTH__AUTHENTICATE_USER_SUCCESS';
const SUB__VALIDATE_AUTH_CODE_SUCCESS = 'SUB__VALIDATE_AUTH_CODE_SUCCESS';

const LOGIN__USER_REMEMBER_ME_REQUEST = 'LOGIN__USER_REMEMBER_ME_REQUEST';
const LOGIN__USER_REMEMBER_ME_SUCCESS = 'LOGIN__USER_REMEMBER_ME_SUCCESS';
const LOGIN__USER_REMEMBER_ME_FAILURE = 'LOGIN__USER_REMEMBER_ME_FAILURE';

const SESSION__REFRESH_TOKEN_REQUEST = 'SESSION__REFRESH_TOKEN_REQUEST';
const SESSION__REFRESH_TOKEN_SUCCESS = 'SESSION__REFRESH_TOKEN_SUCCESS';
const SESSION__REFRESH_TOKEN_FAILURE = 'SESSION__REFRESH_TOKEN_FAILURE';

const LOGOUT__USER_SUCCESS = 'LOGOUT__USER_SUCCESS';

const initialState = {
  token: null,
  refresh: null,
  requiresLogin: false,
};

export const actionCreators = {
  // getToken: (rememberMe) =>
  //   api({
  //     types: [LOGIN__USER_REQUEST, LOGIN__USER_SUCCESS, LOGIN__USER_FAILURE],
  //     uri: '/api/jwt',
  //     config: {
  //       method: 'POST',
  //       credentials: 'include',
  //       headers: {
  //         Accept: 'application/json',
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({ rememberMe }),
  //     },
  //     sendJwtBearer: false,
  //   }),

  getTokenWithRememberMe: (token) =>
    api({
      types: [LOGIN__USER_REMEMBER_ME_REQUEST, LOGIN__USER_REMEMBER_ME_SUCCESS, LOGIN__USER_REMEMBER_ME_FAILURE],
      uri: '/api/jwt/remember',
      sendJwtBearer: false,
      config: {
        method: 'POST',
        headers: {
          AUTHORIZATION: `Bearer ${token}`,
        },
      },
    }),

  refreshToken: () => ({
    types: [SESSION__REFRESH_TOKEN_REQUEST, SESSION__REFRESH_TOKEN_SUCCESS, SESSION__REFRESH_TOKEN_FAILURE],
    uri: '/api/jwt/refresh',
    config: {
      method: 'POST',
    },
  }),

  logout: () => ({ type: LOGOUT__USER_SUCCESS }),
};

const getInitialState = () => {
  const state = JSON.parse(localStorage.getItem('auth'));
  if (!state) return null;
  const session = selector({ session: state });
  const serverNow = moment().add('milliseconds', state.server_offset);
  if (moment(session.refresh_expires) < serverNow) return null;
  return state;
};

export const reducer = (state = getInitialState() || initialState, action) => {
  switch (action.type) {
    case LOGIN__USER_REMEMBER_ME_SUCCESS: {
      const auth = { token: action.response.token, refresh: action.response.refresh, requiresLogin: false, tokenDate: moment() };
      localStorage.setItem('auth', JSON.stringify(auth));
      return auth;
    }
    case SUB__VALIDATE_AUTH_CODE_SUCCESS: {
      // if (action.response.rememberMe.length > 0) {
      //   localStorage.setItem('rememberMe', action.response.rememberMe);
      // }
      const auth = { token: action.response.token, refresh: action.response.refresh, requiresLogin: false, tokenDate: moment() };
      localStorage.setItem('auth', JSON.stringify(auth));
      return auth;
    }
    case AUTH__AUTHENTICATE_USER_SUCCESS: {
      if (action.response.rememberMe.length > 0) {
        localStorage.setItem('rememberMe', action.response.rememberMe);
      }
      const auth = { token: action.response.token, refresh: action.response.refresh, requiresLogin: false, tokenDate: moment() };
      localStorage.setItem('auth', JSON.stringify(auth));
      return auth;
    }
    case SESSION__REFRESH_TOKEN_SUCCESS: {
      const auth = { token: action.response.token, refresh: action.response.refresh, requiresLogin: false, tokenDate: moment() };
      localStorage.setItem('auth', JSON.stringify(auth));
      return auth;
    }
    case LOGOUT__USER_SUCCESS: {
      localStorage.removeItem('auth');
      return { ...initialState };
    }
    default:
      return { ...state };
  }
};
